/**
 * Compact HUD chip showing the current level's "author" — a square frame
 * with a circle-cropped 12×12 avatar + truncated @username + cyan UGC
 * corner-mark. Sized so the badge fits on a single HUD row without
 * crowding HP / skills / inventory / pause.
 */

import Phaser from "phaser";

import { PALETTE, UGC_CYAN } from "../config";
import type { AuthorIdentity } from "../types";

import type { AuthorIdentityService } from "../services/AuthorIdentityService";

const BOX_SIZE = 14;
const AVATAR_SIZE = 12;
const NAME_PADDING_LEFT = 3;
const BADGE_WIDTH = 64;
const BADGE_HEIGHT = BOX_SIZE;

export class AuthorBadge {
  readonly container: Phaser.GameObjects.Container;
  private avatar?: Phaser.GameObjects.Image;
  private readonly avatarBox: Phaser.GameObjects.Rectangle;
  private readonly nameLabel: Phaser.GameObjects.Text;

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly identityService: AuthorIdentityService
  ) {
    this.container = scene.add.container(x, y);

    // Square avatar box.
    this.avatarBox = scene.add
      .rectangle(0, 0, BOX_SIZE, BOX_SIZE, PALETTE.authorBadgeBg, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.authorBadgeText);

    // Cyan UGC corner-mark in the top-right corner of the box.
    const cornerMark = scene.add
      .rectangle(BOX_SIZE - 2, 0, 2, 2, UGC_CYAN, 1)
      .setOrigin(0, 0);

    this.nameLabel = scene.add
      .text(BOX_SIZE + NAME_PADDING_LEFT, BOX_SIZE / 2, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.authorBadgeText),
      })
      .setOrigin(0, 0.5);

    this.container.add([this.avatarBox, cornerMark, this.nameLabel]);
  }

  setIdentity(identity: AuthorIdentity): void {
    if (this.avatar) {
      this.avatar.destroy();
      this.avatar = undefined;
    }
    const offset = (BOX_SIZE - AVATAR_SIZE) / 2;
    this.avatar = this.identityService.drawCircleAvatar(
      this.scene,
      offset,
      offset,
      identity,
      AVATAR_SIZE
    );
    this.container.addAt(this.avatar, 1);
    this.nameLabel.setText(this.truncate(identity.username));
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /**
   * Attach a click handler covering the full badge (avatar + name). Used by
   * the runner HUD wiring to open the profile tooltip on click. Idempotent —
   * calling twice replaces the prior handler.
   */
  setOnClick(handler: () => void): void {
    this.container.setSize(BADGE_WIDTH, BADGE_HEIGHT);
    this.container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, BADGE_WIDTH, BADGE_HEIGHT),
      Phaser.Geom.Rectangle.Contains,
    );
    this.container.off("pointerdown");
    this.container.on("pointerdown", handler);
  }

  destroy(): void {
    this.container.destroy();
  }

  private truncate(name: string): string {
    return name.length <= 8 ? `@${name}` : `@${name.slice(0, 7)}…`;
  }

  private toHex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}

export const AUTHOR_BADGE_WIDTH = BADGE_WIDTH;
export const AUTHOR_BADGE_HEIGHT = BADGE_HEIGHT;
